import type { Theme } from '../../shared/themes';
import { hsl, rgba } from './palette';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  gravity: number;
}

interface Popup {
  x: number;
  y: number;
  vy: number;
  life: number;
  maxLife: number;
  text: string;
  color: string;
  size: number;
}

/** Particles, floating score popups, shake and full-screen flashes. */
export class Fx {
  private particles: Particle[] = [];
  private popups: Popup[] = [];
  private shake = 0;
  private shakeDecay = 4;
  private flashColor = '';
  private flash = 0;
  private chroma = 0;

  burst(x: number, y: number, count: number, color: string, opts: Partial<Particle> = {}): void {
    for (let i = 0; i < count; i++) {
      if (this.particles.length > 320) break;
      const a = Math.random() * Math.PI * 2;
      const sp = 40 + Math.random() * 260;
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 60,
        life: 0,
        maxLife: 0.35 + Math.random() * 0.5,
        size: 1.5 + Math.random() * 3.5,
        color,
        gravity: 520,
        ...opts,
      });
    }
  }

  /** Continuous spray from the rear tyre; call every frame while grounded. */
  dust(x: number, y: number, intensity: number, color: string, dt: number): void {
    const n = Math.floor(intensity * 60 * dt);
    for (let i = 0; i < n; i++) {
      if (this.particles.length > 320) break;
      this.particles.push({
        x: x + (Math.random() - 0.5) * 14,
        y,
        vx: (Math.random() - 0.5) * 90,
        vy: -20 - Math.random() * 70,
        life: 0,
        maxLife: 0.25 + Math.random() * 0.35,
        size: 1 + Math.random() * 3,
        color,
        gravity: 260,
      });
    }
  }

  popup(x: number, y: number, text: string, color: string, size = 20): void {
    if (this.popups.length > 24) this.popups.shift();
    this.popups.push({ x, y, vy: -70, life: 0, maxLife: 1.1, text, color, size });
  }

  addShake(amount: number, decay = 4): void {
    this.shake = Math.min(28, this.shake + amount);
    this.shakeDecay = decay;
  }

  addFlash(color: string, amount = 0.5): void {
    this.flashColor = color;
    this.flash = Math.max(this.flash, amount);
  }

  /** 0..1 rainbow fringe, used while sending. */
  setChroma(v: number): void {
    this.chroma = v;
  }

  update(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.life += dt;
      if (p.life >= p.maxLife) {
        this.particles.splice(i, 1);
        continue;
      }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
    for (let i = this.popups.length - 1; i >= 0; i--) {
      const p = this.popups[i]!;
      p.life += dt;
      if (p.life >= p.maxLife) {
        this.popups.splice(i, 1);
        continue;
      }
      p.y += p.vy * dt;
      p.vy *= 0.94;
    }
    this.shake = Math.max(0, this.shake - this.shakeDecay * this.shake * dt - 4 * dt);
    this.flash = Math.max(0, this.flash - dt * 2.4);
  }

  /** Apply before drawing the world. Always pair with ctx.restore(). */
  applyShake(ctx: CanvasRenderingContext2D): void {
    if (this.shake <= 0.05) return;
    ctx.translate((Math.random() - 0.5) * this.shake, (Math.random() - 0.5) * this.shake);
  }

  drawParticles(ctx: CanvasRenderingContext2D): void {
    for (const p of this.particles) {
      const t = p.life / p.maxLife;
      ctx.globalAlpha = 1 - t;
      ctx.fillStyle = p.color;
      const s = p.size * (1 - t * 0.5);
      ctx.fillRect(p.x, p.y, s, s);
    }
    ctx.globalAlpha = 1;
  }

  drawPopups(ctx: CanvasRenderingContext2D): void {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const p of this.popups) {
      const t = p.life / p.maxLife;
      ctx.globalAlpha = t < 0.12 ? t / 0.12 : 1 - Math.max(0, (t - 0.6) / 0.4);
      ctx.font = `900 ${p.size}px "Arial Black", system-ui, sans-serif`;
      ctx.lineWidth = Math.max(2, p.size * 0.18);
      ctx.strokeStyle = 'rgba(6,8,18,0.8)';
      ctx.strokeText(p.text, p.x, p.y);
      ctx.fillStyle = p.color;
      ctx.fillText(p.text, p.x, p.y);
    }
    ctx.globalAlpha = 1;
  }

  /** Speed lines, vignette, flash — everything that sits over the world. */
  drawOverlay(
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    theme: Theme,
    speedRatio: number,
    timeS: number,
  ): void {
    if (speedRatio > 0.62) {
      const k = (speedRatio - 0.62) / 0.38;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = rgba('#ffffff', 0.03 + k * 0.08);
      ctx.lineWidth = 2;
      const cx = w / 2;
      const cy = h * 0.5;
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2 + timeS * 0.6;
        const r0 = Math.min(w, h) * (0.5 + ((i * 0.061 + timeS * 1.6) % 0.34));
        const r1 = r0 + Math.min(w, h) * 0.08 * (0.4 + k);
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
        ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
        ctx.stroke();
      }
      ctx.restore();
    }

    if (this.chroma > 0.01) {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = this.chroma * 0.16;
      const g = ctx.createLinearGradient(0, 0, w, h);
      g.addColorStop(0, hsl(timeS * 120, 90, 60));
      g.addColorStop(1, hsl(timeS * 120 + 140, 90, 60));
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
    }

    const vg = ctx.createRadialGradient(w / 2, h * 0.52, Math.min(w, h) * 0.34, w / 2, h * 0.52, Math.max(w, h) * 0.78);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, `rgba(0,0,0,${0.34 + speedRatio * 0.16})`);
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, w, h);

    if (this.flash > 0.01) {
      ctx.fillStyle = rgba(this.flashColor || theme.accent, this.flash * 0.55);
      ctx.fillRect(0, 0, w, h);
    }
  }

  clear(): void {
    this.particles.length = 0;
    this.popups.length = 0;
    this.shake = 0;
    this.flash = 0;
    this.chroma = 0;
  }
}
